import { noop, toNumber, throttle } from 'lodash'
import { pick, values, path, propEq } from 'ramda'
import { Fragment, memo, useState, useEffect, useCallback, useMemo } from 'react'
import {
  MenuItem,
  useDialogControllers,
  Button as DSButton,
  ButtonLink,
  Badge,
  Text,
  OverflowMenu,
  Stack as DSStack,
  Box as DSBox,
  IconRocket,
  Skeleton,
  Inline,
  TextLinkButton,
  useToast
} from '@machobear/design-system'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory, Link } from 'react-router-dom'
import styled from 'styled-components'
import { useTour } from '@reactour/tour'

import { useQueryClient } from '@tanstack/react-query'
import { checkRestrictedListing } from '@/utils/check-restricted-listing'
import { SponsoredListingProvider } from '@/features/SponsoredListing/modals/sponsor-wizard'
import { useSelector } from '@/hooks/redux'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import { Tour } from '@/components/tour'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  SingleTableRow,
  Accordion,
  AccordionContent,
  AccordionTableRow,
  AccordionHeader,
  TableHeaderText,
  MobileRowWrapper,
  MobileUnitText,
  MobileUnitDetails,
  CellText,
  OptionsWrapper
} from '@/components/listings-table'
import ListingExternalEditNoticeModal from '@/components/listing-external-edit-notice-modal'
import PageTitle from '@/components/page-title'
import { ListingsEmptyListComponent, NoListingsComponent } from '@/components/table-list'
import useMediaQuery from '@/hooks/use-media-query'
import useProfile from '@/hooks/use-profile'
import { groupedListings } from '@/models/listing/selectors'
import { colors, breakpoints, structure } from '@/styles'
import formatDate from '@/utils/format-date'
import { prefixUnitNumber, parseAreaSize, renderListingPrice } from '@/utils/listings'
import { toUnit } from '@/utils/styles'
import { ListingIcon } from '@/components/icons'
import { Box, Stack } from '@/components/structure'
import { LoadingCircle } from '@/components/loading-circle'
import useDelayedMount from '@/hooks/use-delayed-mount'
import { useHighlight } from '@/models/toaster/hooks'
import { showMarkAsRentedModal } from '@/features/Listing/mark-as-rented'
import { showPending } from '@/features/Listing/publish-modal'
import SponsorDialogs from '@/features/SponsoredListing/components/sponsor-dialogs'
import { useDispatch } from '@/hooks/redux'
import useEventListener from '@/hooks/use-event-listener'
import { getEditMlsListingLink } from '@/utils/getEditMlsListingLink'
import queryClient from '@/components/query-client'
import { ShareListingDialogs } from '@/features/Listing/buttons/share/share-modal'
import toBoolean from '@/utils/to-boolean'
import { pushToDatalayer } from '@/utils/push-datalayer'
import TagTooltip from './tag-tooltip'
import MyListingsWrapper from './my-listings-header'
import useMyListings from './hooks/use-my-listings'
import DeleteConfirmationModal from '../CreateListingWizard/form-edit/delete-confirmation-modal'

const isPublished = propEq('state_machine', 'published')

const TableOutterWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    padding-bottom: 40px;
    margin: 0 30px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)} - 56px);
    margin-bottom: 56px;
    margin-left: 0;
    margin-right: 0;
  }
`

const TableWrapper = styled.div`
  flex: 1;
  max-width: 1130px;
  margin: 0 50px;
  height: 100%;
  border: 6px;

  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 30px;
  }

  @media only screen and (max-width: ${breakpoints.desktopSmall}) {
    margin: 0;
  }
`

const MobileFooterButtonWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${colors.white};
  border-top: 1px solid ${colors.mediumLightGrey};
  border-bottom: 1px solid ${colors.mediumLightGrey};
  z-index: 100;

  ${props =>
    props.displayBlock &&
    `
  display: block;
  position: static;
`}
`

const StyledLink = styled(Link)`
  color: ${colors.foregroundLink} !important;
`

const getRoutes = listing => {
  if (listing.state_machine === 'history') {
    return noop()
  } else {
    return listing.state_machine === 'draft'
      ? `/listing/create/${listing.listing_id}/details`
      : `/listing/${listing.listing_id}`
  }
}

const LinkCell = ({ children, listing, justifyContent, groupedListing, ...props }) => {
  const url = getRoutes(listing)
  return (
    <StyledTableCell justifyContent={justifyContent} blueBorder={groupedListing} {...props}>
      <NavLink to={url} alt={`listing-${listing.listing_id}`}>
        {children}
      </NavLink>
    </StyledTableCell>
  )
}

const getBuildingTitle = (building, count) => {
  const { building_name, address, city, state } = building
  return (
    <DSStack space="medium">
      <Text weight="strong" size="standard">
        {building_name || address} ({count} units)
      </Text>
      <Text size="standard">{`${address}, ${city}${state === null ? '' : `, ${state}`}`}</Text>
    </DSStack>
  )
}

const getListingAddress = listing => {
  const { unit_number, address, city, state } = listing
  const unitNumber = unit_number ? `${prefixUnitNumber(unit_number)} - ` : ''
  const fullAddress = `${address}, ${city}, ${state}`
  return (
    <>
      <Text size="standard">
        {unitNumber}
        {fullAddress}
      </Text>
    </>
  )
}

const useHelpers = () => {
  const { t } = useTranslation()
  const session = useProfile()
  const getUnitNumber = useCallback(
    listing => {
      const { unit_number, unit_type_txt_id } = listing
      if (unit_number) return <Text size="standard">{prefixUnitNumber(unit_number)}</Text>
      if (unit_type_txt_id === 'house')
        return <Text size="standard">{t('common.house', 'House')}</Text>
      if (unit_type_txt_id === 'townhouse')
        return <Text size="standard">{t('common.townhouse', 'Townhouse')}</Text>
      return '-'
    },
    [t]
  )

  const getBedrooms = useCallback(
    listing => {
      const { bedrooms } = listing
      if (bedrooms === '0') return t('common.studio', 'Studio')
      return bedrooms
        ? `${bedrooms} ${t('common.bed', 'Bed')}`
        : t('b.components.view.not_avaiable.text', 'N/A')
    },
    [t]
  )

  const getSize = useCallback(
    listing => {
      const unit = session.setting_size_unit
      const unitText =
        unit === 'meter' ? t('b.listing.view.sqm.unit', 'm²') : t('b.listing.view.sqft.unit', 'ft²')

      const na = t('b.components.view.not_avaiable.text', 'N/A')
      const { gr_min_size, gr_max_size, size, gr_unit } = listing

      const getGroupListingSize = () => {
        const isSameSize = gr_min_size === gr_max_size
        return isSameSize
          ? `${gr_min_size ? parseAreaSize(gr_min_size, unit) + ` ${unitText}` : na}`
          : `${parseAreaSize(gr_min_size, unit) || na} - ${
              parseAreaSize(gr_max_size, unit) || na
            } ${unitText}`
      }

      return gr_unit === '1'
        ? getGroupListingSize(listing)
        : `${size ? parseAreaSize(size, unit) + ` ${unitText}` : na}`
    },
    [t, session.setting_size_unit]
  )
  const helpers = useMemo(
    () => ({ getUnitNumber, getBedrooms, getSize }),
    [getUnitNumber, getBedrooms, getSize]
  )
  return helpers
}

const TableHeaderComponent = ({
  displayTablet,
  updateCurrentSort,
  currentSort,
  currentTab,
  showExtraCell
}) => {
  const { t } = useTranslation()
  const isCurrentTabDraft = currentTab === 'draft'
  return (
    <TableHeader>
      <TableRow
        sticky
        top={displayTablet ? structure.header.height + 176 : structure.header.height + 282}
      >
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('unit')
          }}
          active={currentSort.prop === 'unit'}
          ascending={currentSort.direction === 'asc'}
          width={'28%'}
        >
          <TableHeaderText firstCell>
            {t('l.listings.table.column_header.unit_address', 'Unit Address')}
          </TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell width="10%">
          <TableHeaderText>&nbsp;</TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('price')
          }}
          active={currentSort.prop === 'price'}
          ascending={currentSort.direction === 'asc'}
          width={'12%'}
        >
          <TableHeaderText alignLeft>{t('common.price', 'Price')}</TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('date')
          }}
          active={currentSort.prop === 'date'}
          ascending={currentSort.direction === 'asc'}
          width="17%"
        >
          <TableHeaderText alignLeft>
            {t('l.listing.list.available_date.text', 'Available Date')}
          </TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('apps')
          }}
          active={currentSort.prop === 'apps'}
          ascending={currentSort.direction === 'asc'}
          width={'17%'}
        >
          <TableHeaderText>{t('l.listing.list.applications.text', 'Applications')}</TableHeaderText>
        </TableHeaderCell>

        <TableHeaderCell width={'126px'}>
          <TableHeaderText>&nbsp;</TableHeaderText>
        </TableHeaderCell>

        {showExtraCell && (
          <TableHeaderCell width={isCurrentTabDraft ? '20%' : '80px'}>
            <TableHeaderText>&nbsp;</TableHeaderText>
          </TableHeaderCell>
        )}

        <TableHeaderCell width="56px">
          <TableHeaderText lastCell>&nbsp;</TableHeaderText>
        </TableHeaderCell>
      </TableRow>
    </TableHeader>
  )
}
const TableHeaderComponentForIntegration = ({
  displayTablet,
  updateCurrentSort,
  currentSort,
  currentTab,
  showExtraCell
}) => {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language
  const isZh = currentLang === 'zh'
  const isCurrentTabDraft = currentTab === 'draft'

  return (
    <TableHeader>
      <TableRow
        sticky
        top={displayTablet ? structure.header.height + 176 : structure.header.height + 282}
      >
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('unit')
          }}
          active={currentSort.prop === 'unit'}
          ascending={currentSort.direction === 'asc'}
          width={'25%'}
        >
          <TableHeaderText firstCell>
            {t('l.listings.table.column_header.unit_address', 'Unit Address')}
          </TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell width="10%">
          <TableHeaderText>&nbsp;</TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('realtor')
          }}
          active={currentSort.prop === 'realtor'}
          ascending={currentSort.direction === 'asc'}
          width="25%"
        >
          <TableHeaderText alignLeft>
            {t('l.listing.integration_details.brokerage.label.text', 'Brokerage')}
          </TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('price')
          }}
          active={currentSort.prop === 'price'}
          ascending={currentSort.direction === 'asc'}
          width="10%"
        >
          <TableHeaderText alignLeft>{t('common.price', 'Price')}</TableHeaderText>
        </TableHeaderCell>
        <TableHeaderCell
          onClick={() => {
            updateCurrentSort('apps')
          }}
          active={currentSort.prop === 'apps'}
          ascending={currentSort.direction === 'asc'}
          width="12%"
        >
          <TableHeaderText>{t('l.listing.list.applications.text', 'Applications')}</TableHeaderText>
        </TableHeaderCell>
        {!isCurrentTabDraft && (
          <TableHeaderCell width="126px">
            <TableHeaderText>&nbsp;</TableHeaderText>
          </TableHeaderCell>
        )}
        {showExtraCell && (
          <TableHeaderCell width={isZh ? '90px' : '62px'}>
            <TableHeaderText>&nbsp;</TableHeaderText>
          </TableHeaderCell>
        )}

        <TableHeaderCell width="56px">
          <TableHeaderText lastCell>&nbsp;</TableHeaderText>
        </TableHeaderCell>
      </TableRow>
    </TableHeader>
  )
}

const OptionsMenu = ({
  listing,
  listingId,
  currentTab,
  isMobile = false,
  handleBoostListing,
  imported = false,
  visible = false,
  showModal = noop,
  closeModal = noop,
  currentListingTab
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const showToast = useToast()
  const handleMarkRented = () => {
    history.push(`/listing/${listing.listing_id}/mark-rented`)
  }
  const listQueryObject = currentListingTab
    ? { queryKey: ['my-listings', currentListingTab], exact: true }
    : { queryKey: ['my-listings'] }
  const { link } = getEditMlsListingLink(listing.external_info)

  const toggleHide = async () => {
    await dispatch.listing.update({
      listing_id: listing.listing_id,
      is_hidden: currentTab === 'hidden' ? 0 : 1
    })
    queryClient.invalidateQueries(listQueryObject)
  }

  const hideExternalListingNotice = toBoolean(
    useSelector(
      path(['session', 'user_flags', 'skip_edit_your_listing_on_trreb_modal', 'key_value'])
    )
  )

  const handleOnClick = () => {
    hideExternalListingNotice
      ? window.open(link, '_blank')
      : showModal('editListingOutsideDialog')()
  }
  const handleDelete = async () => {
    const { response } = await dispatch.listing.delete(listing.listing_id)
    const listingAddress = `${listing.unit_number ? `${listing.unit_number} - ` : ''}${listing.address}`
    if (response?.ok) {
      showToast({
        message: t('l.listing.edit.listing_deleted_toast.title', {
          address: listingAddress,
          defaultValue: '{{ address }} has been deleted!'
        }),
        description: t(
          'l.listing.edit.listing_deleted_toast.text',
          'If this was done in error, please contact us at support@liv.rent.'
        ),
        tone: 'positive'
      })
      queryClient.invalidateQueries(listQueryObject)
    } else {
      showToast({ message: t('Unable to delete listing'), tone: 'critical' })
    }
  }

  return (
    <>
      <DSBox>
        <OverflowMenu label="Options" id="options">
          {currentTab === 'draft' ? (
            <MenuItem onClick={showModal('deleteListingDialog')}>{t('Delete listing')}</MenuItem>
          ) : (
            <>
              <MenuItem
                onClick={() =>
                  imported
                    ? handleOnClick()
                    : showMarkAsRentedModal({
                        handleInternal: () => handleMarkRented(listing.listing_id),
                        listingId: listing.listing_id,
                        history
                      })
                }
              >
                {t('l.listing.edit.mark_as_rented_modal.title', 'Mark as Rented')}
              </MenuItem>
              <MenuItem onClick={imported ? handleOnClick : toggleHide}>
                {currentTab === 'hidden'
                  ? t('l.listing.view.hide.text', 'Unhide from search')
                  : t('l.listing.view.unhide.text', 'Hide from search')}
              </MenuItem>
              {isMobile && (
                <MenuItem
                  onClick={handleBoostListing}
                  id={`listings-table-sponsor-listing-boost-click-${listingId}`}
                >
                  {t('l.mobile.listings.overflow_menu.boost_listing.text', 'Boost Listing')}
                </MenuItem>
              )}
            </>
          )}
        </OverflowMenu>
      </DSBox>
      <ListingExternalEditNoticeModal
        visible={visible.editListingOutsideDialog}
        closeModal={closeModal('editListingOutsideDialog')}
        externalInfo={listing?.external_info}
      />
      <DeleteConfirmationModal
        visible={visible.deleteListingDialog}
        closeModal={closeModal('deleteListingDialog')}
        handleDelete={handleDelete}
      />
    </>
  )
}

const MobileListingRow = ({
  listing,
  groupedListing,
  singleListing,
  currentTab,
  currentListingTab,
  profileData
}) => {
  const { t } = useTranslation()
  const { getUnitNumber } = useHelpers()
  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    editListingOutsideDialog: false,
    mainDialog: false,
    emailDialog: false,
    phoneDialog: false,
    craigslistDialog: false,
    kijijiDialog: false,
    facebookDialog: false
  })

  const published = isPublished(listing)
  const isVerificationRequired = listing.verification_message_state === 'pending'
  const imported = checkRestrictedListing(listing.tags)

  const isBillingUserValid = profileData?.billing_user_valid
  const handleBoostListing = () => {
    if (isBillingUserValid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  return (
    <>
      <LinkCell colSpan="8" listing={listing} width="70%">
        <MobileRowWrapper indent={groupedListing}>
          <MobileUnitText>
            {singleListing ? getListingAddress(listing) : getUnitNumber(listing)}
          </MobileUnitText>
          <MobileUnitDetails>
            <p>{renderListingPrice(listing)}</p>
            <p>
              {t('l.listing.list.count_applied.title', {
                defaultValue: '{{ applicationCount }} {{ applications }}',
                applicationCount: toNumber(listing.applications_count),
                applications:
                  toNumber(listing.applications_count) === 1 ? 'Application' : 'Applications'
              })}
            </p>
          </MobileUnitDetails>
        </MobileRowWrapper>
      </LinkCell>
      <>
        <StyledTableCell colSpan="1" listing={listing} verticalAlign="top">
          <DSBox paddingY="small">
            <Inline space="xsmall" align="right" alignY="top">
              <DSStack align="left" space="xxsmall">
                {listing.state_machine === 'pending' ? (
                  <DSBox
                    onClick={e => {
                      e.preventDefault()
                      showPending({
                        isPublished: published,
                        isVerificationRequired,
                        shareListing: () => showModal('mainDialog')()
                      })
                    }}
                    cursor="pointer"
                    role="button"
                  >
                    <Badge tone="caution" weight="strong" size="small">
                      {t('l.listing.view.pending.text', 'Pending')}
                    </Badge>
                  </DSBox>
                ) : null}
                {imported ? <TagTooltip listing={listing} /> : null}
              </DSStack>

              <DSBox marginRight="small" marginTop="xxsmall">
                <MobileUnitText link>
                  <OptionsMenu
                    listing={listing}
                    listingId={listing.id}
                    currentTab={currentTab}
                    showModal={showModal}
                    closeModal={closeModal}
                    visible={visible}
                    handleBoostListing={handleBoostListing}
                    isMobile
                    imported={imported}
                    currentListingTab={currentListingTab}
                  />
                </MobileUnitText>
              </DSBox>
            </Inline>
          </DSBox>
        </StyledTableCell>
        <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
          <SponsorDialogs
            listingId={listing.listing_id}
            visible={visible}
            showModal={showModal}
            closeModal={closeModal}
          />
        </SponsoredListingProvider>
        <ShareListingDialogs
          listingId={listing.listing_id}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
        />
      </>
    </>
  )
}

const ListingRow = ({
  listing,
  groupedListing,
  singleListing,
  currentTab,
  showExtraCell,
  currentListingTab,
  profileData
}) => {
  const { t } = useTranslation()
  const isCurrentTabDraft = currentTab === 'draft'
  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    editListingOutsideDialog: false,
    deleteListingDialog: false
  })

  const published = isPublished(listing)
  const { getUnitNumber } = useHelpers()
  const listingId = listing.listing_id
  const isVerificationRequired = listing.verification_message_state === 'pending'
  const isBillingUserValid = profileData?.billing_user_valid
  const handleBoostListing = () => {
    if (isBillingUserValid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  return (
    <>
      <LinkCell listing={listing} groupedListing={groupedListing} justifyContent="flex-start">
        <CellText firstCell indent={groupedListing}>
          {singleListing ? getListingAddress(listing) : getUnitNumber(listing)}
        </CellText>
      </LinkCell>
      <LinkCell listing={listing}>
        {listing.state_machine === 'pending' ? (
          <DSBox
            onClick={e => {
              e.preventDefault()
              showPending({
                isPublished: published,
                isVerificationRequired,
                shareListing: () => showModal('mainDialog')()
              })
            }}
            cursor="pointer"
            role="button"
          >
            <Badge tone="caution" weight="strong">
              {t('l.listing.view.pending.text', 'Pending')}
            </Badge>
          </DSBox>
        ) : (
          <CellText />
        )}
      </LinkCell>
      <LinkCell listing={listing} justifyContent="flex-start">
        <CellText textAlign="left">
          <Text size="standard">{renderListingPrice(listing)}</Text>
        </CellText>
      </LinkCell>
      <LinkCell width={10} listing={listing} justifyContent="flex-start">
        <CellText textAlign="left">
          <Text size="standard">
            {formatDate(listing.availability, 'MMM D, YYYY') ||
              t('b.components.view.not_avaiable.text', 'N/A')}
          </Text>
        </CellText>
      </LinkCell>
      <StyledTableCell>
        <CellText top={0} bottom={0}>
          {isCurrentTabDraft ? (
            <Text tone="neutral">{listing.applications_count}</Text>
          ) : (
            <StyledLink
              to={`/listing/${listingId}/applications`}
              id={`listings-table-applications-click-${listingId}`}
              role="link"
            >
              {listing.applications_count}
            </StyledLink>
          )}
        </CellText>
      </StyledTableCell>
      {!isCurrentTabDraft ? (
        <>
          <StyledTableCell>
            <CellText>
              <DSButton
                variant="solid"
                state="default"
                size="small"
                label="Boost Listing"
                icon={<IconRocket />}
                iconPosition="leading"
                onClick={handleBoostListing}
                id={`listings-table-sponsor-listing-boost-click-${listingId}`}
              >
                {t('l.listings.boost_listing.text', 'Boost')}
              </DSButton>
            </CellText>
          </StyledTableCell>
          <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
            <SponsorDialogs
              listingId={listingId}
              visible={visible}
              showModal={showModal}
              closeModal={closeModal}
            />
          </SponsoredListingProvider>
        </>
      ) : (
        <StyledTableCell />
      )}
      {showExtraCell && (
        <LinkCell listing={listing} justifyContent="flex-end">
          <CellText link margin="0">
            <ButtonLink
              href={
                currentTab === 'draft' ? `/listing/create/${listingId}` : `/listing/${listingId}`
              }
              variant="ghost"
              state="default"
              tone="default"
              size="small"
              label={
                currentTab === 'draft'
                  ? t('l.listing.list.view.text', 'View')
                  : t('l.listing.list.manage.text', 'Manage')
              }
              id={`listings-table-view-click-${listingId}`}
            >
              {currentTab === 'draft'
                ? t('l.listing.list.view.text', 'View')
                : t('l.listing.list.manage.text', 'Manage')}
            </ButtonLink>
          </CellText>
        </LinkCell>
      )}

      <StyledTableCell justifyContent={'center'} listing={listing}>
        <OptionsWrapper>
          <OptionsMenu
            listing={listing}
            listingId={listingId}
            currentTab={currentTab}
            currentListingTab={currentListingTab}
            showModal={showModal}
            closeModal={closeModal}
            visible={visible}
          />
        </OptionsWrapper>
      </StyledTableCell>
    </>
  )
}
const ListingRowForIntegration = ({
  listing,
  groupedListing,
  singleListing,
  currentTab,
  showExtraCell,
  profileData,
  currentListingTab
}) => {
  const { t } = useTranslation()
  const isCurrentTabDraft = currentTab === 'draft'
  const { visible, showModal, closeModal } = useDialogControllers({
    billingUser: false,
    sponsoredListing: false,
    nextSteps: false,
    checkoutDialog: false,
    editPaymentDialog: false,
    editListingOutsideDialog: false
  })
  const published = isPublished(listing)
  const { getUnitNumber } = useHelpers()
  const listingId = listing.listing_id
  const isVerificationRequired = listing.verification_message_state === 'pending'
  const isBillingUserValid = profileData?.billing_user_valid
  const handleBoostListing = () => {
    if (isBillingUserValid) {
      showModal('sponsoredListing')()
    } else {
      showModal('billingUser')()
    }
  }

  return (
    <>
      <LinkCell listing={listing} groupedListing={groupedListing} justifyContent="flex-start">
        <CellText firstCell indent={groupedListing}>
          {singleListing ? getListingAddress(listing) : getUnitNumber(listing)}
        </CellText>
      </LinkCell>
      <LinkCell listing={listing}>
        <DSBox paddingX="xsmall">
          <DSStack space="xxsmall" align="left">
            {listing.state_machine === 'pending' && (
              <DSBox
                onClick={e => {
                  e.preventDefault()
                  showPending({
                    isPublished: published,
                    isVerificationRequired,
                    shareListing: () => showModal('mainDialog')()
                  })
                }}
                cursor="pointer"
                role="button"
              >
                <Badge tone="caution" weight="strong">
                  {t('l.listing.view.pending.text', 'Pending')}
                </Badge>
              </DSBox>
            )}

            <TagTooltip listing={listing} />
          </DSStack>
        </DSBox>
      </LinkCell>
      <StyledTableCell listing={listing} justifyContent="flex-start">
        <CellText textAlign="left" link>
          <TextLinkButton
            weight="regular"
            onClick={() => window.open(listing.external_info.external_url, '_blank')}
            target="_blank"
          >
            {listing.external_info.realtor_name}
          </TextLinkButton>
        </CellText>
      </StyledTableCell>
      <LinkCell listing={listing} justifyContent="flex-start">
        <CellText textAlign="left">
          <Text size="standard">{renderListingPrice(listing)}</Text>
        </CellText>
      </LinkCell>

      <StyledTableCell>
        <CellText top={0} bottom={0}>
          {isCurrentTabDraft ? (
            <Text tone="neutral">{listing.applications_count}</Text>
          ) : (
            <StyledLink
              to={`/listing/${listingId}/applications`}
              id={`listings-table-applications-click-${listingId}`}
              role="link"
            >
              {listing.applications_count}
            </StyledLink>
          )}
        </CellText>
      </StyledTableCell>
      {!isCurrentTabDraft && (
        <>
          <StyledTableCell>
            <CellText>
              <DSButton
                variant="solid"
                state="default"
                size="small"
                label="Boost Listing"
                icon={<IconRocket />}
                iconPosition="leading"
                onClick={handleBoostListing}
                id={`listings-table-sponsor-listing-boost-click-${listingId}`}
              >
                {t('l.listings.boost_listing.text', 'Boost')}
              </DSButton>
            </CellText>
          </StyledTableCell>
          <SponsoredListingProvider initialStep={0} initialValues={{}} isCreateListing={false}>
            <SponsorDialogs
              listingId={listingId}
              visible={visible}
              showModal={showModal}
              closeModal={closeModal}
            />
          </SponsoredListingProvider>
        </>
      )}
      {showExtraCell && (
        <LinkCell listing={listing}>
          <CellText link margin={0} paddingRight={0}>
            <ButtonLink
              href={`/listing/${listingId}`}
              variant="ghost"
              state="default"
              tone="default"
              size="small"
              label="View"
              id={`listings-table-view-click-${listingId}`}
            >
              {t('common.view', 'View')}
            </ButtonLink>
          </CellText>
        </LinkCell>
      )}

      <StyledTableCell listing={listing}>
        <CellText link>
          <OptionsMenu
            listing={listing}
            listingId={listingId}
            currentTab={currentTab}
            showModal={showModal}
            closeModal={closeModal}
            visible={visible}
            handleBoostListing={handleBoostListing}
            imported
            currentListingTab={currentListingTab}
          />
        </CellText>
      </StyledTableCell>
    </>
  )
}

const draftStates = ['draft']
const publishedState = ['published', 'pending']
const ignoredListingStates = ['history', 'upcoming', 'rented']

const resetHighlightEnabled = () => {
  let hasHighlight = false // Declare mutable variable
  return listing => {
    if (hasHighlight) return false // If we already have highlighted on this render, skip others
    hasHighlight = listing.state_machine !== 'pending' // Set globalish variable to true if not pending, otherwise keep false
    return hasHighlight // Return value
  }
}

const SkeletonState = ({ currentTab, isMobile, showExtraCell }) => {
  const StyledSkeletonRow = () => {
    return (
      <>
        {isMobile ? (
          <SingleTableRow>
            <StyledTableCell colSpan="8" width="85%">
              <DSBox display="flex" flexDirection="column" gap="xxsmall" padding="xsmall">
                <Skeleton.Shape width={288} height={24} />
                <Skeleton.Shape width={186} height={24} />
              </DSBox>
            </StyledTableCell>
            <StyledTableCell colSpan="1" width="15%">
              <DSBox>
                <Skeleton.Shape width={32} height={32} />
              </DSBox>
            </StyledTableCell>
          </SingleTableRow>
        ) : (
          <SingleTableRow>
            <StyledTableCell colSpan="2">
              <DSBox width="full" padding="xsmall">
                <Skeleton.Shape width="95%" height={24} />
              </DSBox>
            </StyledTableCell>
            <StyledTableCell>
              <DSBox width="full" padding="xsmall">
                <Skeleton.Shape width="95%" height={24} />
              </DSBox>
            </StyledTableCell>
            <StyledTableCell>
              <DSBox width="full" padding="xsmall">
                <Skeleton.Shape width="95%" height={24} />
              </DSBox>
            </StyledTableCell>
            <StyledTableCell>
              <DSBox width="full" padding="xsmall">
                <Skeleton.Shape width="95%" height={24} />
              </DSBox>
            </StyledTableCell>
            <StyledTableCell>
              <DSBox width="full" padding="xsmall">
                <Skeleton.Shape width="95%" height={24} />
              </DSBox>
            </StyledTableCell>
            {currentTab !== 'draft' && (
              <StyledTableCell>
                <DSBox width="full" padding="xsmall">
                  <Skeleton.Shape width="95%" height={24} />
                </DSBox>
              </StyledTableCell>
            )}
            {showExtraCell && (
              <StyledTableCell>
                <DSBox width="full" padding="xsmall">
                  <Skeleton.Shape width="95%" height={24} />
                </DSBox>
              </StyledTableCell>
            )}

            {currentTab !== 'draft' && (
              <StyledTableCell>
                <DSBox width="full" padding="xsmall">
                  <Skeleton.Shape width="95%" height={24} />
                </DSBox>
              </StyledTableCell>
            )}
          </SingleTableRow>
        )}
      </>
    )
  }

  const SKELETON_ROWS = 7

  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <StyledSkeletonRow key={'listing-skeleton-row-' + index} />
      ))}
    </>
  )
}

const LoadingState = () => {
  return (
    <DSBox display="flex" justifyContent="center" padding="xlarge">
      <LoadingCircle />
    </DSBox>
  )
}

const empty = []

const MyListings = props => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const profileData = useProfile()
  const applicationTourId = useSelector(
    path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract', 'listing_id'])
  )
  const showApplicationTour = listing =>
    typeof applicationTourId !== 'undefined' && listing.listing_id === applicationTourId
  const [currentTab, setCurrentTab] = useState('published')
  const isCurrentTabDraft = currentTab === 'draft'
  const [currentListingTab, setCurrentListingTab] = useState('livrent')
  const [tabCount, setTabCount] = useState({
    draft: 0,
    published: 0,
    hidden: 0
  })
  const [filteredData, setFilteredData] = useState([])
  const [tabFilteredData, setTabFilteredData] = useState([])
  const [currentLoadedData, setCurrentLoadedData] = useState([])
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [searchString, setSearchString] = useState('')
  const [isDataChanging, setIsDataChanging] = useState(false)
  const [rowsLoading, setRowsLoading] = useState(false)

  useEffect(() => {
    if (!rowsLoading) return
    loadData()
  }, [rowsLoading])

  const loadData = () => {
    setCurrentLoadedData(
      currentLoadedData.concat(
        tabFilteredData.slice(currentLoadedData.length, currentLoadedData.length + 20)
      )
    )
  }

  const onScroll = useMemo(
    () =>
      throttle(async event => {
        const el = event
        const scrollTop = el.target.children[0].scrollTop
        const scrollHeight = el.target.children[0].scrollHeight - el.target.children[0].clientHeight
        const ratio = scrollTop / scrollHeight
        if (ratio > 0.85 && currentLoadedData.length < tabFilteredData.length) {
          setRowsLoading(true)
        }
      }, 300),
    [currentLoadedData.length, tabFilteredData.length]
  )
  useEventListener('scroll', onScroll)

  const getHighlightEnable = resetHighlightEnabled()

  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop})`)

  const { listings = empty, isLoading } = useMyListings(currentListingTab)

  useEffect(() => {
    if (!listings) return
    setFilteredData(groupedListings(listings))
  }, [listings])

  // Calculate count for each tab
  useEffect(() => {
    const calculateTabCounts = filtered => {
      let draft = 0
      let published = 0
      let hidden = 0
      filtered.forEach(building => {
        building.listings.forEach(listing => {
          if (listing.is_hidden === '1' && !ignoredListingStates.includes(listing.state_machine)) {
            hidden++
          } else if (draftStates.includes(listing.state_machine)) {
            draft++
          } else if (publishedState.includes(listing.state_machine)) {
            published++
          }
        })
      })
      setTabCount({ draft, published, hidden })
    }
    calculateTabCounts(filteredData)
  }, [filteredData, currentListingTab])

  const noListings = listings.length === 0 && listings !== empty

  // Handle tab changes
  const handleSetTabDraft = useCallback(() => {
    const filtered = filteredData
      .map(building => ({
        ...building,
        listings: building.listings.filter(
          listing => draftStates.includes(listing.state_machine) && listing.is_hidden !== '1'
        )
      }))
      .filter(building => building.listings && building.listings.length > 0)
    setTabFilteredData(filtered)
    setCurrentLoadedData(filtered.slice(0, 20))
  }, [filteredData, currentListingTab])
  const handleSetTabPublished = useCallback(() => {
    const filtered = filteredData
      .map(building => ({
        ...building,
        listings: building.listings.filter(
          listing => publishedState.includes(listing.state_machine) && listing.is_hidden !== '1'
        )
      }))
      .filter(building => building.listings && building.listings.length > 0)
    setTabFilteredData(filtered)
    setCurrentLoadedData(filtered.slice(0, 20))
  }, [filteredData, currentListingTab])
  const handleSetTabHidden = useCallback(() => {
    const filtered = filteredData
      .map(building => ({
        ...building,
        listings: building.listings.filter(
          listing =>
            listing.is_hidden === '1' && !ignoredListingStates.includes(listing.state_machine)
        )
      }))
      .filter(building => building.listings && building.listings.length > 0)
    setTabFilteredData(filtered)
    setCurrentLoadedData(filtered.slice(0, 20))
  }, [filteredData])

  useEffect(() => {
    setIsDataChanging(true)
    if (isCurrentTabDraft) {
      handleSetTabDraft()
    } else if (currentTab === 'published') {
      handleSetTabPublished()
    } else if (currentTab === 'hidden') {
      handleSetTabHidden()
    }
    if (!isDataLoaded && !isLoading) {
      setIsDataLoaded(true)
    }
  }, [
    currentTab,
    handleSetTabDraft,
    handleSetTabHidden,
    handleSetTabPublished,
    isDataLoaded,
    isLoading,
    currentListingTab,
    isCurrentTabDraft
  ])

  // Handle search
  const searchListings = searchString => {
    setSearchString(searchString)
    setIsDataChanging(true)
    const filtered = listings
      .map(building => ({
        ...building,
        listings: building.listings.filter(listing => {
          const searchFields = values(
            pick(['unit_number', 'address', 'building_name', 'city', 'state'], listing)
          ).concat(listing.external_info?.realtor_name ? [listing.external_info.realtor_name] : [])
          return JSON.stringify(searchFields)
            .toLowerCase()
            .includes(searchString.trim().toLowerCase())
        })
      }))
      .filter(building => building.listings && building.listings.length > 0)
    setFilteredData(filtered)
  }

  // Handle Tour
  const { setIsOpen, isOpen, setCurrentStep } = useTour()

  const toggleTour = () => {
    if (noListings) {
      isMobile ? setCurrentStep(11) : setCurrentStep(9)
    } else if (tabCount.published === 0) {
      if (tabCount.draft === 0) {
        setCurrentTab('hidden')
        setCurrentStep(10)
      } else {
        setCurrentTab('draft')
        setCurrentStep(10)
      }
    } else {
      isMobile ? setCurrentStep(6) : setCurrentStep(3)
    }

    pushToDatalayer({ tourId: 'sllp-react' })
    setIsOpen(true)
  }

  useEffect(() => {
    setIsOpen(false)
    const storedData = JSON.parse(localStorage.getItem('sponsored-tour'))
    const totalListings = filteredData.reduce((total, obj) => total + obj.listings.length, 0)

    const tabsLoaded = tabCount.draft + tabCount.hidden + tabCount.published === totalListings
    if (
      storedData &&
      storedData?.listingsTable === false &&
      isDataLoaded &&
      !isLoading &&
      tabsLoaded
    ) {
      toggleTour()
    }
  }, [isDataLoaded, isLoading, tabCount])
  useEffect(() => {
    setSearchString('')
  }, [currentListingTab])
  const [currentSort, setCurrentSort] = useState({
    prop: 'none',
    direction: 'none'
  })

  //sort listings within a building
  const orderListings = building => {
    if (currentSort.prop === 'price') {
      currentSort.direction === 'asc'
        ? building.listings.sort((a, b) => Number(b.price) - Number(a.price))
        : building.listings.sort((a, b) => Number(a.price) - Number(b.price))
    } else if (currentSort.prop === 'unit') {
      building.listings.sort((a, b) => {
        if (a.unit_number > b.unit_number) {
          return currentSort.direction === 'asc' ? -1 : 1
        } else if (a.unit_number < b.unit_number) {
          return currentSort.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    } else if (currentSort.prop === 'date') {
      currentSort.direction === 'asc'
        ? building.listings.sort((a, b) => new Date(b.availability) - new Date(a.availability))
        : building.listings.sort((a, b) => new Date(a.availability) - new Date(b.availability))
    } else {
      currentSort.direction === 'asc'
        ? building.listings.sort(
            (a, b) => Number(b.applications_count) - Number(a.applications_count)
          )
        : building.listings.sort(
            (a, b) => Number(a.applications_count) - Number(b.applications_count)
          )
    }

    return building
  }
  //sort listings within a building and single listings
  useEffect(() => {
    if (currentSort.prop === 'price') {
      const sortedData = currentLoadedData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? Number(b.listings[0].price) - Number(a.listings[0].price)
            : Number(a.listings[0].price) - Number(b.listings[0].price)
        })
      setCurrentLoadedData(sortedData)
    } else if (currentSort.prop === 'unit') {
      const sortedData =
        currentSort.direction === 'asc'
          ? currentLoadedData
              .map(building => orderListings(building))
              .sort((a, b) => {
                return b.listings[0].unit_number - a.listings[0].unit_number
              })
          : currentLoadedData
              .map(building => orderListings(building))
              .sort((a, b) => {
                if (a.listings[0].address > b.listings[0].address) {
                  return -1
                } else if (a.listings[0].address < b.listings[0].address) {
                  return 1
                }
                return 0
              })
      setCurrentLoadedData(sortedData)
    } else if (currentSort.prop === 'date') {
      const sortedData = currentLoadedData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? new Date(b.listings[0].availability) - new Date(a.listings[0].availability)
            : new Date(a.listings[0].availability) - new Date(b.listings[0].availability)
        })
      setCurrentLoadedData(sortedData)
    } else if (currentSort.prop === 'apps') {
      const sortedData = currentLoadedData
        .map(building => orderListings(building))
        .sort((a, b) => {
          return currentSort.direction === 'asc'
            ? Number(b.listings[0].applications_count) - Number(a.listings[0].applications_count)
            : Number(a.listings[0].applications_count) - Number(b.listings[0].applications_count)
        })
      setCurrentLoadedData(sortedData)
    } else {
      setCurrentLoadedData(tabFilteredData.slice(0, currentLoadedData.length))
    }
  }, [currentSort.direction, currentSort.prop])

  const updateCurrentSort = propName => {
    if (currentSort.direction === 'asc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: propName,
        direction: 'desc'
      })
    } else if (currentSort.direction === 'desc' && currentSort.prop === propName) {
      setCurrentSort({
        prop: 'none',
        direction: 'none'
      })
    } else {
      setCurrentSort({
        prop: propName,
        direction: 'asc'
      })
    }
  }

  useEffect(() => {
    setIsDataChanging(false)
    setRowsLoading(false)
  }, [currentLoadedData])

  const ComponentLists = {
    livrent: ListingRow,
    integrations: ListingRowForIntegration
  }

  const TableRowComponent = isMobile ? MobileListingRow : ComponentLists[currentListingTab]

  return (
    <DSBox background="white" style={{ padding: '0 0 min(100vh - 506px, 100px)', height: '100%' }}>
      <Helmet>
        <title>{t('l.listing.list.listings.title', 'Listings')}</title>
      </Helmet>
      <PageTitle>
        <ListingIcon />
        {t('l.listing.list.listings.title', 'Listings')}
      </PageTitle>

      <MyListingsWrapper
        tabCount={tabCount}
        currentListingTab={currentListingTab}
        setCurrentListingTab={setCurrentListingTab}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        onSearch={searchListings}
        setSearchString={setSearchString}
        searchString={searchString}
      />

      <TableOutterWrapper>
        <TableWrapper>
          <Table>
            {!isMobile &&
              (currentListingTab === 'integrations' ? (
                <TableHeaderComponentForIntegration
                  displayTablet={displayTablet}
                  updateCurrentSort={updateCurrentSort}
                  currentSort={currentSort}
                  currentTab={currentTab}
                  showExtraCell={isDesktop}
                />
              ) : (
                <TableHeaderComponent
                  displayTablet={displayTablet}
                  updateCurrentSort={updateCurrentSort}
                  currentSort={currentSort}
                  currentTab={currentTab}
                  showExtraCell={isDesktop}
                />
              ))}

            {isLoading ? (
              <SkeletonState currentTab={currentTab} isMobile={isMobile} />
            ) : !noListings ? (
              <TableBody className="listings-table-draft">
                {isDataChanging ? (
                  <SkeletonState
                    currentTab={currentTab}
                    isMobile={isMobile}
                    showExtraCell={isDesktop}
                  />
                ) : currentLoadedData.length > 0 ? (
                  currentLoadedData.map((building, index) => {
                    const listingCount = building.listings.length
                    if (listingCount === 1) {
                      const listing = building.listings[0]
                      return (
                        <Fragment key={`${listing.listing_id}-${index}`}>
                          <SingleTableRow
                            className={`listings-table-mobile-${index} listings-table-${index}`}
                            onClick={() => {
                              if (isOpen) setIsOpen(false)
                            }}
                            data-testid={listing.listing_id}
                          >
                            <TableRowComponent
                              listing={listing}
                              singleListing
                              index={index}
                              currentTab={currentTab}
                              queryClient={queryClient}
                              showExtraCell={isDesktop}
                              currentListingTab={currentListingTab}
                              profileData={profileData}
                            />
                          </SingleTableRow>
                          <HighlightWrapper
                            enableFind={getHighlightEnable(listing)}
                            enableApp={showApplicationTour(listing)}
                            applicationTourId={applicationTourId}
                            onClick={() => props.history.push(getRoutes(listing))}
                          />
                        </Fragment>
                      )
                    }
                    return (
                      <Accordion key={`${building.building_id}-${index}`} expanded>
                        <AccordionHeader
                          colSpan={isMobile ? 9 : isDesktop ? 8 : 7}
                          listingsView={!isMobile && !isCurrentTabDraft}
                          unitCount={building.listings.length}
                          isMobile={isMobile}
                        >
                          {getBuildingTitle(building, listingCount)}
                        </AccordionHeader>
                        <AccordionContent>
                          <>
                            {building.listings.map((listing, index) => {
                              return (
                                <Fragment key={`${listing.listing_id}-${index}`}>
                                  <AccordionTableRow
                                    data-testid={listing.listing_id}
                                    className={`listings-table-mobile-${index}`}
                                  >
                                    <TableRowComponent
                                      listing={listing}
                                      groupedListing
                                      currentTab={currentTab}
                                      queryClient={queryClient}
                                      showExtraCell={isDesktop}
                                      profileData={profileData}
                                      currentListingTab={currentListingTab}
                                    />
                                  </AccordionTableRow>
                                  <HighlightWrapper
                                    enableFind={getHighlightEnable(listing)}
                                    enableApp={showApplicationTour(listing)}
                                    applicationTourId={applicationTourId}
                                    onClick={() => props.history.push(getRoutes(listing))}
                                  />
                                </Fragment>
                              )
                            })}
                          </>
                        </AccordionContent>
                      </Accordion>
                    )
                  })
                ) : searchString.length > 0 ? (
                  <TableRow>
                    <StyledTableCell colSpan={8} rounded>
                      <NoListingsComponent />
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={8} rounded>
                      <ListingsEmptyListComponent currentListingTab={currentListingTab} />
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            ) : (
              <TableBody className="listings-table-empty">
                <TableRow>
                  <StyledTableCell colSpan={8} rounded>
                    <ListingsEmptyListComponent currentListingTab={currentListingTab} />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableWrapper>
      </TableOutterWrapper>
      {!isLoading && isMobile && currentListingTab === 'livrent' && (
        <MobileFooterButtonWrapper className="listings-table-empty-mobile">
          <DSBox padding="xsmall">
            <Link to="/listing/create">
              <DSButton
                id="application-create-new-listing-click"
                variant="solid"
                state="default"
                tone="default"
                size="small"
                label="Create new listing"
              >
                {t('l.listing.list.create_new_listing.button', 'Create new listing')}
              </DSButton>
            </Link>
          </DSBox>
        </MobileFooterButtonWrapper>
      )}
      {rowsLoading && <LoadingState />}
    </DSBox>
  )
}

const ApplicationHighlight = ({ onBeforeClose, t, applicationTourId }) => {
  const { hide, next, skip } = useHighlight('application.trust_score', 'listing', 'application')

  return (
    <Highlight
      title={t('b.toaster.view.application.trust_score.listing.tour.title', 'Select a listing')}
      description={t(
        'b.toaster.view.application.trust_score.listing.tour.description',
        `Click "View" to see your listing's dashboard, which includes the list of applications.`
      )}
      hide={hide}
      onBeforeClose={canceled => onBeforeClose(canceled, next, skip)}
      t={t}
      selector={`.view-listing-btn-id-${applicationTourId}`}
    />
  )
}

const FindTenantsHighlight = ({ onBeforeClose, t }) => {
  const { hide, next, skip } = useHighlight('find_tenants.find_tenants', 'listing', 'suggested')

  return (
    <Highlight
      title={t('b.toaster.view.find_tenants.listing.tour.title', 'Suggested Tenants')}
      description={t(
        'b.toaster.view.find_tenants.listing.tour.description',
        `Click "View" to see your listing's dashboard, which includes our list of suggested tenants.`
      )}
      hide={hide}
      onBeforeClose={canceled => onBeforeClose(canceled, next, skip)}
      t={t}
      selector=".view-listing-btn-0"
    />
  )
}

const Highlight = ({ children, hide, title, description, onBeforeClose, selector, t }) => {
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const delay = useDelayedMount(1000)
  const steps = useMemo(
    () => [
      {
        selector,
        content: ({ next, cancel }) => (
          <Stack gutter={16} maxWidth={216}>
            <Stack gutter={4}>
              <Label fontSize="14px">{title}</Label>
              <Box color={colors.grey32}>
                <Label fontSize="12px" type="small">
                  {description}
                </Label>
              </Box>
            </Stack>
            <Box width="100%" display="flex" justify="flex-end">
              <Box>
                <Button size="small" theme="text" onClick={cancel}>
                  {t('common.skip', 'Skip')}
                </Button>
              </Box>
              <Box width={96}>
                <Button size="small" onClick={next}>
                  {t('common.next', 'Next')}
                </Button>
              </Box>
            </Box>
          </Stack>
        )
      }
    ],
    [t, title, description, selector]
  )
  return (
    <Tour
      hide={hide || !delay}
      steps={steps}
      point={displayMobile ? 'left' : 'right'}
      position={displayMobile ? 'right' : 'left'}
      showCloseButton
      onBeforeClose={onBeforeClose}
    >
      {children}
    </Tour>
  )
}

const HighlightWrapper = ({ enableFind, enableApp, applicationTourId, onClick }) => {
  const { t } = useTranslation()
  const onBeforeClose = (canceled, next, skip) => {
    if (!canceled) {
      next()
    } else {
      skip()
    }
    if (!canceled) onClick()
  }

  if (!enableFind && !enableApp) return <></>
  return (
    <>
      {enableApp && (
        <ApplicationHighlight
          t={t}
          onBeforeClose={onBeforeClose}
          applicationTourId={applicationTourId}
        />
      )}
      {enableFind && <FindTenantsHighlight t={t} onBeforeClose={onBeforeClose} />}
    </>
  )
}

export default memo(MyListings)
